import React, { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ModalName } from 'app/enums/modal-name';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { AvailableBalance } from '../AvailableBalance';
import { useStyles } from '../PaymentModal.styles';
import { useSwapForm } from 'modules/payments/hooks/use-swap-form';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { SwapForm } from './SwapForm';
import { SwapSummary } from './SwapSummary';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { useCancelToken } from 'app/services/api/hooks';
import { SwapExchangeRateDto } from 'app/interfaces/dto/swap-exchange-rate.dto';
import { modalsReducers } from 'app/store/modal/slice';
import { toastSuccess } from 'modules/utils';
import { getSwapExchangeRates } from 'modules/payments/api/get-swap-exchange-rates';
import { getSwapSummary } from 'modules/payments/api/get-swap-summary';
import { confirmSwap } from 'modules/payments/api/confirm-swap';
import { SwapSummaryDto } from 'app/interfaces/dto';
import moment from 'moment';
import { RemainingTime } from 'shared/components/RemainingTime';
import { getSwapQuotas } from 'modules/payments/api/get-swap-quotas';
import { UserQuotaStatusDto } from 'app/interfaces/dto/user-quota-status.dto';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { Backdrop } from 'shared/components/Backdrop';

const EXCHANGE_RATE_REFRESH_DELAY = 100;
const EXCHANGE_RATES_AUTO_LOAD_COUNT_LIMIT = 5;
const EXCHANGE_RATES_AUTO_LOAD_MINUTES_TIMEOUT = 10;

const SwapModalComponent: FC = () => {
  const { classes } = useStyles();
  const cancelToken = useCancelToken();
  const dispatch = useDispatch();
  const userAccounts = useSelector(userPaymentAccountsSelector.all);

  const exchangeRatesAutoLoadCount = useRef(0);
  const lastSummaryLoad = useRef<undefined | ReturnType<typeof moment>>();

  const [isSwapLoading, setSwapLoading] = useState(false);
  const [summary, setSummary] = useState<SwapSummaryDto | undefined>();

  const [exchangeRates, setExchangeRates] = useState<SwapExchangeRateDto[] | undefined>();
  const [areRatesLoading, setRatesLoading] = useState(false);

  const [quotas, setQuotas] = useState<UserQuotaStatusDto | undefined>();
  const [areQuotasLoading, setQuotasLoading] = useState(false);

  const suggestedBuyAmount: number | undefined = useSelector(
    modalsSelectorFactory.useModalPayload(ModalName.BalanceSwap)
  );

  const { form } = useSwapForm(
    () => userAccounts,
    () => quotas
  );

  const {
    formState: { isValid, isValidating, isDirty },
    watch,
    getValues,
    reset: resetForm,
    setValue,
    trigger,
  } = form;

  useEffect(() => {
    if (!suggestedBuyAmount) return;

    setValue('buy', suggestedBuyAmount, { shouldValidate: true });
    setValue('selectedSide', 'buyingCurrency');
  }, [setValue, suggestedBuyAmount]);

  useEffect(() => {
    resetForm(getValues());
    trigger('sell');
  }, [getValues, resetForm, quotas, userAccounts, trigger]);

  const [pair, sell, buy, selectedSide] = watch(['pair', 'sell', 'buy', 'selectedSide']);

  const currentExchange = useMemo(
    () => exchangeRates?.find((rate) => rate.name === pair),
    [exchangeRates, pair]
  );
  const exchangeRate = currentExchange?.rate;

  const isCalculateSummaryDisabled =
    isSwapLoading || areRatesLoading || areQuotasLoading || !isValid || isValidating;
  const isConfirmDisabled = isCalculateSummaryDisabled || !summary;

  const closeModal = useCallback(
    () => dispatch(modalsReducers.closeModal(ModalName.BalanceSwap)),
    [dispatch]
  );

  const closeModalIfNotActive = useCallback(() => {
    exchangeRatesAutoLoadCount.current++;
    if (
      exchangeRatesAutoLoadCount.current >= EXCHANGE_RATES_AUTO_LOAD_COUNT_LIMIT &&
      (!lastSummaryLoad.current ||
        moment().diff(lastSummaryLoad.current, 'minute') >=
          EXCHANGE_RATES_AUTO_LOAD_MINUTES_TIMEOUT)
    ) {
      toastSuccess('Swap modal closed due to inactivity');
      closeModal();
      return true;
    }

    return false;
  }, [closeModal]);

  const loadExchangeRates = useCallback(() => {
    if (closeModalIfNotActive()) return;

    setRatesLoading(true);
    const result = getSwapExchangeRates({ cancelToken });
    result.then(setExchangeRates).finally(() => setRatesLoading(false));
    return result;
  }, [cancelToken, closeModalIfNotActive]);

  useEffect(() => {
    loadExchangeRates();
  }, [loadExchangeRates]);

  const loadSwapQuotas = useCallback(() => {
    if (!MultiCurrencyConfig.isUserQuotaSwapEnabled()) return;

    setQuotasLoading(true);
    getSwapQuotas({ cancelToken })
      .then((data) => setQuotas(data))
      .finally(() => setQuotasLoading(false));
  }, [cancelToken]);

  useEffect(() => {
    loadSwapQuotas();
  }, [loadSwapQuotas]);

  useEffect(() => {
    if (!currentExchange?.expiresAt) return;

    const milliseconds =
      moment(currentExchange.expiresAt).diff(moment(), 'millisecond') + EXCHANGE_RATE_REFRESH_DELAY;
    const timeoutId = setTimeout(loadExchangeRates, milliseconds);

    return () => clearTimeout(timeoutId);
  }, [currentExchange?.expiresAt, loadExchangeRates]);

  const loadSummary = useCallback(() => {
    if (isCalculateSummaryDisabled) return Promise.reject();
    if (exchangeRate === undefined) return Promise.reject();

    const requestedAmount = selectedSide === 'sellingCurrency' ? { sell } : { buy };

    setSwapLoading(true);

    const result = getSwapSummary({
      cancelToken,
      exchangeRate,
      pair,
      ...requestedAmount,
    });
    result
      .then(
        (data) => {
          setSummary(data);
          resetForm(getValues());
        },
        () => setSummary(undefined)
      )
      .finally(() => setSwapLoading(false));
    return result;
  }, [
    buy,
    cancelToken,
    exchangeRate,
    getValues,
    isCalculateSummaryDisabled,
    pair,
    resetForm,
    selectedSide,
    sell,
  ]);

  const handleLoadSummary = useCallback(() => {
    loadSummary().then(() => {
      lastSummaryLoad.current = moment();
    });
  }, [loadSummary]);

  const handleConfirm = useCallback(() => {
    if (isConfirmDisabled) return;
    if (exchangeRate === undefined) return;

    const requestedAmount = selectedSide === 'sellingCurrency' ? { sell } : { buy };

    setSwapLoading(true);

    confirmSwap({ cancelToken, exchangeRate, pair, ...requestedAmount })
      .then(() => {
        closeModal();
        toastSuccess(
          'Swap request is sent. Your balance will be updated soon. See details in Transactions page.'
        );
      })
      .finally(() => setSwapLoading(false));
  }, [buy, cancelToken, closeModal, exchangeRate, isConfirmDisabled, pair, selectedSide, sell]);

  const expirationDate = useMemo(() => {
    if (!currentExchange?.expiresAt && !isConfirmDisabled) {
      return '';
    }

    const diff = moment(currentExchange?.expiresAt).diff(moment(), 'second');
    return moment(moment().toISOString()).add(diff, 'seconds').toISOString();
  }, [currentExchange?.expiresAt, isConfirmDisabled]);

  useEffect(() => {
    setSummary(undefined);
  }, [pair]);

  useEffect(() => {
    if (summary?.exchangeRate === undefined || summary?.exchangeRate === exchangeRate) return;

    loadSummary();
  }, [exchangeRate, summary?.exchangeRate, loadSummary]);

  return (
    <Modal
      name={ModalName.BalanceSwap}
      title="Swap"
      contentHeight={540}
      maxWidth="md"
      actions={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
        >
          <LoaderButton
            loading={isSwapLoading}
            disabled={isConfirmDisabled}
            fullWidth
            size="medium"
            color="primary"
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm Swap
          </LoaderButton>
          {currentExchange?.expiresAt && !isConfirmDisabled ? (
            <Box mt={1} display="flex">
              <Typography variant="subtitle2" color="text.primary" pr={0.5}>
                Time left to confirm
              </Typography>
              <Typography variant="subtitle2">
                <RemainingTime date={expirationDate} />
              </Typography>
            </Box>
          ) : null}
        </Box>
      }
    >
      <Box className={classes.root}>
        <AvailableBalance onlyActive={false} />

        <Box mb={2}>
          {!quotas || !userAccounts ? (
            <Backdrop />
          ) : (
            <SwapForm form={form} isDisabled={isSwapLoading} exchangeRate={exchangeRate} />
          )}
        </Box>

        {!summary || isDirty ? (
          <Box mb={2}>
            <LoaderButton
              onClick={handleLoadSummary}
              disabled={isCalculateSummaryDisabled}
              fullWidth
              size="medium"
              color="primary"
              variant="outlined"
            >
              Calculate Swap
            </LoaderButton>
          </Box>
        ) : null}

        {summary ? (
          <>
            <Box mb={2} display="flex" alignItems="baseline">
              <Typography mr={1} variant="h6">
                Summary:
              </Typography>
              {isDirty ? (
                <>
                  <Typography>(outdated, click calculate again)</Typography>
                </>
              ) : null}
            </Box>
            <SwapSummary {...summary} pair={pair} />
          </>
        ) : null}
      </Box>
    </Modal>
  );
};

export const SwapModal = withModalOpenChecker(SwapModalComponent, ModalName.BalanceSwap);
